import React from 'react';
import { useParams } from 'react-router-dom';
import { logEvent } from '../firebase';

const IOS_URL = 'https://apps.apple.com/app/pholtr/id6753979622';
const ANDROID_URL = 'https://play.google.com/store/apps/details?id=nu.hibou.prodshot';

function capitalize(str) {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

export default function Influencer() {
  const { name } = useParams();
  const displayName = capitalize(name);
  const promoCode = name.toUpperCase();
  const [copied, setCopied] = React.useState(false);

  React.useEffect(() => {
    logEvent('influencer_page_view', { influencer: name });
  }, [name]);

  const handleCopy = () => {
    navigator.clipboard.writeText(promoCode).then(() => {
      setCopied(true);
      logEvent('influencer_code_copied', { influencer: name, code: promoCode });
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleAppStoreClick = () => {
    logEvent('influencer_appstore_click', { influencer: name, store: 'ios' });
  };

  const handlePlayStoreClick = () => {
    logEvent('influencer_appstore_click', { influencer: name, store: 'android' });
  };

  const isAndroid = /Android/i.test(navigator.userAgent);

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <h1 style={styles.logo}>pholtr</h1>

        <p style={styles.greeting}>Hey {displayName}!</p>

        <h2 style={styles.headline}>Your exclusive access is ready.</h2>

        <p style={styles.body}>
          Make your Vinted listings stand out — professional product photos in seconds,
          straight from your phone.
        </p>

        <div style={styles.codeBox} onClick={handleCopy} title="Tap to copy">
          <span style={styles.codeLabel}>YOUR CODE</span>
          <span style={styles.code}>{promoCode}</span>
          <span style={styles.copyHint}>{copied ? 'Copied!' : 'Tap to copy'}</span>
        </div>

        <div style={styles.storeButtons}>
          {!isAndroid && (
            <a
              href={IOS_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleAppStoreClick}
            >
              <img
                src="/app-store-badge.svg"
                alt="Download on the App Store"
                style={styles.badge}
              />
            </a>
          )}
          {isAndroid && (
            <a
              href={ANDROID_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={handlePlayStoreClick}
            >
              <img
                src="/google-play-badge.svg"
                alt="Get it on Google Play"
                style={styles.badge}
              />
            </a>
          )}
          {!isAndroid && (
            <a
              href={ANDROID_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={handlePlayStoreClick}
            >
              <img
                src="/google-play-badge.svg"
                alt="Get it on Google Play"
                style={styles.badge}
              />
            </a>
          )}
        </div>

        <p style={styles.hint}>Copy your code above, then enter it in the app after downloading.</p>
      </div>

      <footer style={styles.footer}>
        <a href="/terms" style={styles.footerLink}>Terms</a>
        <span style={styles.dot}>·</span>
        <a href="/privacy" style={styles.footerLink}>Privacy</a>
      </footer>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    backgroundColor: '#f8f9fa',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '2rem 1rem',
    fontFamily: 'system-ui, -apple-system, sans-serif',
  },
  card: {
    backgroundColor: '#ffffff',
    borderRadius: '24px',
    padding: '2.5rem 2rem',
    maxWidth: '420px',
    width: '100%',
    textAlign: 'center',
    boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
    border: '1px solid rgba(0,0,0,0.04)',
  },
  logo: {
    fontFamily: 'MomoTrustDisplay, sans-serif',
    fontSize: '2.25rem',
    margin: '0 0 1.75rem 0',
    color: '#000',
    letterSpacing: '-0.02em',
  },
  greeting: {
    fontSize: '1rem',
    color: '#6B7280',
    margin: '0 0 0.5rem 0',
    fontWeight: '500',
  },
  headline: {
    fontSize: '1.6rem',
    fontWeight: '700',
    color: '#111827',
    margin: '0 0 1rem 0',
    letterSpacing: '-0.02em',
    lineHeight: 1.25,
  },
  body: {
    fontSize: '0.95rem',
    color: '#6B7280',
    lineHeight: 1.6,
    margin: '0 0 1.75rem 0',
  },
  codeBox: {
    backgroundColor: '#f8f9fa',
    border: '2px dashed #e5e7eb',
    borderRadius: '16px',
    padding: '1.25rem 1rem',
    marginBottom: '1.75rem',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '0.35rem',
    transition: 'border-color 0.2s ease',
    userSelect: 'none',
  },
  codeLabel: {
    fontSize: '0.7rem',
    fontWeight: '600',
    letterSpacing: '0.1em',
    color: '#9CA3AF',
    textTransform: 'uppercase',
  },
  code: {
    fontSize: '1.5rem',
    fontWeight: '700',
    color: '#111827',
    letterSpacing: '0.04em',
  },
  copyHint: {
    fontSize: '0.75rem',
    color: '#FF5722',
    fontWeight: '500',
  },
  storeButtons: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: '0.75rem',
    marginBottom: '1.25rem',
  },
  badge: {
    height: '52px',
    width: 'auto',
    display: 'block',
    borderRadius: '8px',
  },
  hint: {
    fontSize: '0.8rem',
    color: '#9CA3AF',
    margin: 0,
    lineHeight: 1.5,
  },
  footer: {
    marginTop: '2rem',
    fontSize: '0.8rem',
    color: '#9CA3AF',
    display: 'flex',
    gap: '0.5rem',
    alignItems: 'center',
  },
  footerLink: {
    color: '#9CA3AF',
    textDecoration: 'none',
  },
  dot: {
    color: '#D1D5DB',
  },
};
